import React, { useEffect, useMemo } from "react";
import SmartAppBanner from "./SmartAppBanner";
import { HelmetProvider } from "react-helmet-async";

// routes.ts
const BUSINESS_PATTERNS = [
    "/bopen",
    "/verify-business/*",
    "/reset-business-password/*",
    "/b/*",
];

const CLIENT_PATTERNS = [
    "/copen",
    "/verify-client/*",
    "/reset-client-password/*",
    "/bs/*",
    "/tm/*",
    "/c/*",
    "/m/*",
];

const resolveTrackingRedirectBase = () => {
    if (process.env.REACT_APP_TRACKING_REDIRECT_BASE) {
        return process.env.REACT_APP_TRACKING_REDIRECT_BASE.replace(/\/+$/, "");
    }
    return process.env.REACT_APP_ENV === "production"
        ? "https://terminka.com"
        : "https://app-dev.terminka.com";
};

export const resolveAppointmentAccessRedirectUrl = ({pathname, search, hash}) => {
    const match = pathname.match(/^\/(?:appt|c)\/(manage|review)\/([A-Za-z0-9_-]+)\/?$/);
    if (!match) return null;

    const [, action, token] = match;
    const params = new URLSearchParams(search);
    if (action === "review") {
        params.set("review", "1");
    }

    const query = params.toString();
    return `${resolveTrackingRedirectBase()}/bs/appointment-access/${token}${query ? `?${query}` : ""}${hash || ""}`;
};

// простейший матч с поддержкой суффиксного "/*"
const matchPattern = (pattern: string, path: string) => {
    if (pattern.endsWith("/*")) {
        const prefix = pattern.slice(0, -1); // обрежем звездочку
        return path.startsWith(prefix);
    }
    return pattern === path;
};

export const resolveAppByPath = (rawPath: string) => {
    const path = rawPath.split("?")[0].replace(/\/+$/, "") || "/"; // без query и финального "/"
    if (BUSINESS_PATTERNS.some((p) => matchPattern(p, path))) return "business";
    if (CLIENT_PATTERNS.some((p) => matchPattern(p, path))) return "client";
    // дефолт: клиентское
    return "client";
};

// если в приложении пути совпадают с веб-роутами — можно вернуть как есть.
// тут оставляю задел, чтобы переименовать пути, если нужно:
export const mapWebPathToAppPath = (type: "business" | "client", webPath: string) => {
    // пример: если веб "/verify-business/abc" → app "bs/verify/abc"
    // иначе просто возвращаем webPath без ведущего слеша
    return webPath.replace(/^\/+/, "");
};


function App() {
    // на клиенте можно взять текущий путь
    const appointmentAccessRedirectUrl = useMemo(
        () => resolveAppointmentAccessRedirectUrl(window.location),
        []
    );
    const isMarketingTrackingPath = useMemo(
        () => /^\/m\/[^/]+/.test(window.location.pathname),
        []
    );
    const type = useMemo(() => resolveAppByPath(window.location.pathname), []);

    useEffect(() => {
        if (!appointmentAccessRedirectUrl) return;
        window.location.replace(appointmentAccessRedirectUrl);
    }, [appointmentAccessRedirectUrl]);

    useEffect(() => {
        if (!isMarketingTrackingPath || appointmentAccessRedirectUrl) return;
        const redirectBase = resolveTrackingRedirectBase();
        window.location.replace(
            `${redirectBase}${window.location.pathname}${window.location.search}${window.location.hash}`
        );
    }, [appointmentAccessRedirectUrl, isMarketingTrackingPath]);

    if (appointmentAccessRedirectUrl || isMarketingTrackingPath) {
        return null;
    }

    // определяем окружение
    const isProduction = process.env.NODE_ENV === 'production' &&
        process.env.REACT_APP_ENV === 'production';
    const androidSuffix = isProduction ? '' : 'dev';

    console.log('App: type=', type, ', androidSuffix=', androidSuffix);

    // пропишите реальные ID/пакеты
    const config =
        type === "business"
            ? {
                appName: "Terminka Partner",
                iosAppStoreId: "6751897686",        // <-- ID бизнес-приложения в App Store
                androidPackage: `com.terminka.business${androidSuffix ? `.${androidSuffix}` : ''}`,
                scheme: `terminkabusiness${androidSuffix ? `-${androidSuffix}` : ''}://`,
            }
            : {
                appName: "Terminka Client",
                iosAppStoreId: "6751916033",        // <-- ID клиентского приложения
                androidPackage: `com.terminka.client${androidSuffix ? `.${androidSuffix}` : ''}`,
                scheme: `terminkaclient${androidSuffix ? `-${androidSuffix}` : ''}://`,
            };



    return (
        <HelmetProvider>
            <SmartAppBanner
                appName={config.appName}
                iosAppStoreId={config.iosAppStoreId}
                androidPackage={config.androidPackage}
                deepLink={(platform) => {
                    const webPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                    const appPath = mapWebPathToAppPath(type, webPath);
                    return `${config.scheme}open/${appPath}`;
                }}
                campaignParams={{ inviterId: "ABC123", campaign: "summer" }}
                autoTryOpen={false}
                dismissTtlHours={24}
            />
            <div className="App">
                <div style={{ padding: 10, margin: "auto", textAlign: "center" }}>
                    <img src="/assets/main_image.png" alt="Main image" width={500} />
                </div>
            </div>
        </HelmetProvider>
    );
}

export default App;
